import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Restaurant, BusinessType } from "@/model/entities";
import * as Yup from "yup";
import { useFormik } from "formik";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import useAppStore from "@/store/appStore";
import { uploadImage } from "@/service/firebaseUtils";
import UploadImagePreview from "../input/UploadImagePreview";
import UploadImageInput from "../input/UploadImageInput";
import SubdomainInput from "../input/SubdomainInput";

interface EditRestaurantModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  restaurant: Restaurant;
}

const EditRestaurantModal = ({
  open,
  onOpenChange,
  onSuccess,
  restaurant,
}: EditRestaurantModalProps) => {
  const { t } = useTranslation();
  const { direction } = useAppStore();
  const [currentStep, setCurrentStep] = useState(1);

  const validationSchema = Yup.object().shape({
    restaurantName: Yup.string().required(t("input.required")),
    subdomain: Yup.string()
      .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, t("input.subdomainInvalid"))
      .required(t("input.required")),
    ownerPhoneNumber: Yup.string()
      .matches(/^[0-9+\-() ]+$/, t("input.phoneInvalid"))
      .required(t("input.required")),
    plan: Yup.string().required(t("input.required")),
    address: Yup.string().required(t("input.required")),
    phone: Yup.string()
      .matches(/^[0-9+\-() ]+$/, t("input.phoneInvalid"))
      .required(t("input.required")),
    businessType: Yup.string()
      .oneOf(Object.values(BusinessType))
      .required(t("input.required")),
    restaurantLogo: Yup.mixed().notRequired(),
  });

  const formik = useFormik({
    initialValues: {
      restaurantName: restaurant.restaurantName,
      subdomain: restaurant.subdomain || "",
      ownerPhoneNumber: restaurant.ownerPhoneNumber || "",
      plan: restaurant.plan || "basic",
      address: restaurant.address || "",
      phone: restaurant.phone,
      businessType: restaurant.businessType,
      restaurantLogo: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        let logoUrl = restaurant.restaurantLogo || "";
        if (values.restaurantLogo) {
          const path = `restaurants/${restaurant.id}/logo`;
          logoUrl = await uploadImage(values.restaurantLogo, path);
        }

        const updatedData = {
          ...values,
          restaurantLogo: logoUrl,
        };

        await updateDoc(doc(db, "restaurants", restaurant.id!), updatedData);
        onOpenChange(false);
        setCurrentStep(1);
        onSuccess();
        toast.success(t("restaurantUpdatedSuccess"));
      } catch (error) {
        console.error("Error updating restaurant:", error);
        toast.error(t("restaurantUpdatedError"));
      }
    },
  });

  const handleNext = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = (e: React.MouseEvent) => {
    e.preventDefault();
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="restaurantName">{t("restaurantName")}</Label>
        <Input
          id="restaurantName"
          value={formik.values.restaurantName}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t("input.restaurantNamePlaceholder")}
        />
        {formik.touched.restaurantName && formik.errors.restaurantName && (
          <div className="text-red-500 text-sm">
            {formik.errors.restaurantName}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="subdomain">{t("input.subdomainPlaceholder")}</Label>
        <SubdomainInput
          value={formik.values.subdomain}
          onChange={(e) => formik.setFieldValue("subdomain", e.target.value)}
          onBlur={formik.handleBlur}
          error={formik.touched.subdomain ? formik.errors.subdomain : undefined}
          placeholder={t("input.subdomainPlaceholder")} name={restaurant.restaurantName}        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="ownerPhoneNumber">{t("signup.ownerPhoneNumber")}</Label>
        <Input
          id="ownerPhoneNumber"
          value={formik.values.ownerPhoneNumber}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t("input.ownerPhoneNumberPlaceholder")}
        />
        {formik.touched.ownerPhoneNumber && formik.errors.ownerPhoneNumber && (
          <div className="text-red-500 text-sm">
            {formik.errors.ownerPhoneNumber}
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="restaurantLogo">{t("profile.restaurantLogo")}</Label>
        <UploadImageInput
          onFileChange={(file) => formik.setFieldValue("restaurantLogo", file)}
        />
        {formik.touched.restaurantLogo && formik.errors.restaurantLogo && (
          <div className="text-red-500 text-sm">
            {formik.errors.restaurantLogo}
          </div>
        )}
        <UploadImagePreview
          currentImageUrl={restaurant.restaurantLogo}
          newImageFile={formik.values.restaurantLogo}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="plan">{t("selectPlan")}</Label>
        <Select
          value={formik.values.plan}
          onValueChange={(value) => formik.setFieldValue("plan", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("input.selectPlanPlaceholder")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="basic">{t("plans.basic")}</SelectItem>
            <SelectItem value="pro">{t("plans.pro")}</SelectItem>
            <SelectItem value="enterprise">{t("plans.enterprise")}</SelectItem>
          </SelectContent>
        </Select>
        {formik.touched.plan && formik.errors.plan && (
          <div className="text-red-500 text-sm">{formik.errors.plan}</div>
        )}
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="address">{t("address")}</Label>
        <Input
          id="address"
          value={formik.values.address}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t("input.addressPlaceholder")}
        />
        {formik.touched.address && formik.errors.address && (
          <div className="text-red-500 text-sm">{formik.errors.address}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="phone">{t("phone")}</Label>
        <Input
          id="phone"
          type="tel"
          value={formik.values.phone}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          placeholder={t("input.phonePlaceholder")}
        />
        {formik.touched.phone && formik.errors.phone && (
          <div className="text-red-500 text-sm">{formik.errors.phone}</div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="businessType">{t("businessType")}</Label>
        <Select
          value={formik.values.businessType}
          onValueChange={(value) => formik.setFieldValue("businessType", value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t("input.selectBusinessType")} />
          </SelectTrigger>
          <SelectContent>
            {Object.values(BusinessType).map((type) => (
              <SelectItem key={type} value={type}>
                {t(`businessTypes.${type}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {formik.touched.businessType && formik.errors.businessType && (
          <div className="text-red-500 text-sm">
            {formik.errors.businessType}
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t("editRestaurant", "Edit Restaurant")}</DialogTitle>
          <DialogDescription>
            {t("editRestaurantDescription", "Update the restaurant details")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={formik.handleSubmit}>
          <div className="py-4">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
          </div>
          <DialogFooter className="flex justify-between">
            {currentStep > 1 && (
              <Button variant="outline" onClick={handleBack}>
                {direction === "rtl" ? (
                  <ArrowRight className="mr-2 h-4 w-4" />
                ) : (
                  <ArrowLeft className="mr-2 h-4 w-4" />
                )}
                {t("buttons.previous")}
              </Button>
            )}
            {currentStep < 3 ? (
              <Button type="button" onClick={handleNext}>
                {t("buttons.next")}
                {direction === "rtl" ? (
                  <ArrowLeft className="ml-2 h-4 w-4" />
                ) : (
                  <ArrowRight className="ml-2 h-4 w-4" />
                )}
              </Button>
            ) : (
              <Button type="submit" disabled={formik.isSubmitting || !formik.dirty}>
                {t("buttons.save")}
              </Button>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditRestaurantModal;
